import type { ImageRecord } from '~/utils/imageDb'
import { getWidgetConfig } from '../runtime/widgetConfig'
import { isHeicBlob, toJpegFileName } from '../utils/imageFormat'
import { resolveFrozenOrientation } from '../utils/persistedOrientation'
import { emitUploadEvent } from '../utils/uploadBus'
import { resolveUploadDeviceInfo } from '../utils/deviceInfo'

export type UploadRecordResult = {
    id: string
    remoteUrl: string
    recordPatch?: Partial<ImageRecord>
    /** Datei liegt im Speicher, der Server erzeugt aber noch Vorschauen (kein remoteUrl, kein Fehler). */
    processing?: boolean
}

class PartialBatchUploadError extends Error {
    partialResults: UploadRecordResult[]
    failedIds: string[]

    constructor(message: string, partialResults: UploadRecordResult[], failedIds: string[]) {
        super(message)
        this.name = 'PartialBatchUploadError'
        this.partialResults = partialResults
        this.failedIds = failedIds
    }
}

type DirectUploadPayload = {
    rec: ImageRecord
    blob: Blob | File
    meta: ReturnType<typeof buildUploadMeta> & {
        mime: string
        bytes: number
    }
}

type PresignBatchItemResponse =
    | {
        existing: true
        item: any
    }
    | {
        existing: false
        id: string
        pathOriginal: string
        uploadUrl: string
        uploadMethod: string
        uploadHeaders?: Record<string, string>
        preview?: {
            path: string
            uploadUrl: string
            uploadMethod: string
            uploadHeaders?: Record<string, string>
        } | null
    }

const settledRemoteDeleteIds = new Set<string>()
const pendingRemoteDeleteIds = new Set<string>()
// Cap unbounded growth of the delete-id tracking sets. Set iteration order is
// insertion order, so dropping the first entries removes the oldest ids.
const REMOTE_DELETE_ID_CAP = 5000
function pruneRemoteDeleteSet(set: Set<string>) {
    if (set.size <= REMOTE_DELETE_ID_CAP) return
    const overflow = set.size - REMOTE_DELETE_ID_CAP
    let removed = 0
    for (const id of set) {
        set.delete(id)
        if (++removed >= overflow) break
    }
}
const SINGLE_UPLOAD_TIMEOUT_MS = 180_000
const BATCH_UPLOAD_TIMEOUT_MS = 240_000
const COMPLETE_BATCH_CHUNK_SIZE = 10
const COMPLETE_BATCH_FLUSH_DELAY_MS = 120
let directUploadAvailability: 'unknown' | 'available' | 'unavailable' = 'unknown'
let legacyBackendQueue: Promise<void> = Promise.resolve()
let activeDirectPutCount = 0
const uploadProgressState = new Map<string, { at: number; pct: number; uploadedBytes: number }>()

type DirectUploadMode = 'auto' | 'prefer' | 'require'

function editorBaseUrl() {
    const cfg = getWidgetConfig() as any
    const raw = String(cfg?.editorBaseUrl || 'https://www.pixelfoto-express.de/editor').replace(/\/+$/, '')
    return raw
}

export async function duplicateEditorUpload(input: {
    sourceId: string
    projectCode: string
    id: string
    name?: string
    categoryId?: number
    productId?: number
    meta?: Record<string, any> | null
}) {
    const sourceId = String(input?.sourceId || '').trim()
    const projectCode = String(input?.projectCode || '').trim()
    const id = String(input?.id || '').trim()
    if (!sourceId || !projectCode || !id) {
        throw new Error('duplicate upload requires sourceId, projectCode and id')
    }

    const res = await fetchWithTimeout(
        `${editorBaseUrl()}/upload/${encodeURIComponent(sourceId)}/duplicate`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                projectCode,
                id,
                ...(String(input?.name || '').trim() ? { name: String(input.name).trim() } : {}),
                ...(Number(input?.categoryId || 0) > 0 ? { categoryId: Number(input?.categoryId || 0) } : {}),
                ...(Number(input?.productId || 0) > 0 ? { productId: Number(input?.productId || 0) } : {}),
                ...(input?.meta && typeof input.meta === 'object' ? { meta: input.meta } : {}),
            }),
        },
        SINGLE_UPLOAD_TIMEOUT_MS,
    )

    if (!res.ok) {
        const txt = await res.text().catch(() => '')
        throw new Error(`Duplicate upload failed (${res.status}): ${txt || res.statusText}`)
    }

    return await res.json().catch(() => null)
}

function directUploadMode(): DirectUploadMode {
    const cfg = getWidgetConfig() as any
    const raw = String(cfg?.editorDirectUploadMode || '').trim().toLowerCase()
    if (raw === 'prefer' || raw === 'require') return raw
    return 'auto'
}

function isDirectUploadRequired() {
    return directUploadMode() === 'require'
}

function uploadDebugEnabled() {
    return true
}

function uploadDebug(label: string, payload?: any) {
    if (!uploadDebugEnabled()) return
    if (payload === undefined) {
        console.log(label)
        return
    }
    console.log(label, payload)
}

function directPutConcurrency(): number {
    return 6
}

function resolveAdaptiveDirectPutConcurrency(records: ImageRecord[]): number {
    const cfg = getWidgetConfig() as any
    const raw = Number(cfg?.editorDirectPutConcurrency || 0)
    if (Number.isFinite(raw) && raw > 0) return Math.max(1, Math.min(16, Math.round(raw)))

    const nav = typeof navigator !== 'undefined' ? navigator : null
    const connection = (nav as any)?.connection || (nav as any)?.mozConnection || (nav as any)?.webkitConnection || null
    const effectiveType = String(connection?.effectiveType || '').toLowerCase()
    const saveData = !!connection?.saveData
    const isMobile =
        typeof window !== 'undefined'
            ? window.matchMedia?.('(max-width: 820px)')?.matches || false
            : false
    const avgBytes = records.length
        ? records.reduce((sum, rec) => sum + (Number((rec as any)?.bytes || 0) || 0), 0) / records.length
        : 0
    const hardware = Number(nav?.hardwareConcurrency || 0) || 0
    const ua = String(nav?.userAgent || '').toLowerCase()
    const isWindows = ua.includes('windows')
    const isFirefox = ua.includes('firefox')
    const isEdge = ua.includes('edg/')

    if (saveData || effectiveType === 'slow-2g' || effectiveType === '2g') return 2

    let limit = isMobile ? 1 : 4
    if ((isWindows && isFirefox) || (isWindows && isEdge)) limit = Math.min(limit, 2)
    else if (isFirefox) limit = Math.min(limit, 3)
    if (!isMobile && effectiveType === '4g' && hardware >= 8) limit = 5
    if (!isMobile && effectiveType === '4g' && hardware >= 12 && avgBytes > 0 && avgBytes <= 4_000_000) limit = 6
    if (effectiveType === '3g') limit = Math.min(limit, 3)
    if (avgBytes >= 18_000_000) limit = Math.min(limit, isMobile ? 1 : 3)
    else if (avgBytes >= 10_000_000) limit = Math.min(limit, isMobile ? 1 : 4)
    if (records.length >= 48) limit = Math.min(limit, isMobile ? 1 : 3)
    else if (records.length >= 24) limit = Math.min(limit, isMobile ? 1 : 4)
    else if (records.length >= 12) limit = Math.min(limit, isMobile ? 1 : 5)

    if ((isWindows && isFirefox) || (isWindows && isEdge)) {
        if (records.length >= 24) limit = 2
        if (avgBytes >= 8_000_000) limit = 1
    }

    const minLimit = isMobile ? 1 : (((isWindows && isFirefox) || (isWindows && isEdge)) ? 1 : 2)
    return Math.max(minLimit, Math.min(6, limit))
}

function shouldEmitProgressPatch(id: string, uploadedBytes: number, totalBytes: number, progressPct: number) {
    const now = Date.now()
    const prev = uploadProgressState.get(id)
    const pctDelta = Math.abs(progressPct - Number(prev?.pct || 0))
    const byteDelta = Math.abs(uploadedBytes - Number(prev?.uploadedBytes || 0))
    const isFinal = totalBytes > 0 && uploadedBytes >= totalBytes
    const shouldEmit =
        !prev ||
        isFinal ||
        pctDelta >= 2 ||
        byteDelta >= 262_144 ||
        (now - prev.at) >= 140

    if (!shouldEmit) return false
    uploadProgressState.set(id, { at: now, pct: progressPct, uploadedBytes })
    if (isFinal) uploadProgressState.delete(id)
    return true
}

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit, timeoutMs: number) {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), timeoutMs)
    try {
        return await fetch(input, {
            ...init,
            signal: ctrl.signal,
        })
    } finally {
        clearTimeout(timer)
    }
}

async function runThroughLegacyBackendQueue<T>(work: () => Promise<T>): Promise<T> {
    const previous = legacyBackendQueue
    let release!: () => void
    legacyBackendQueue = new Promise<void>((resolve) => {
        release = resolve
    })

    await previous
    try {
        return await work()
    } finally {
        release()
    }
}

async function mapWithConcurrency<T>(items: T[], concurrency: number, work: (item: T, index: number) => Promise<void>) {
    const wanted = Math.max(1, Math.min(items.length || 1, concurrency))
    let cursor = 0
    await Promise.all(
        Array.from({ length: wanted }, async () => {
            while (cursor < items.length) {
                const index = cursor++
                await work(items[index]!, index)
            }
        }),
    )
}

function chunkArray<T>(items: T[], size: number): T[][] {
    const out: T[][] = []
    const chunkSize = Math.max(1, size)
    for (let i = 0; i < items.length; i += chunkSize) out.push(items.slice(i, i + chunkSize))
    return out
}

function nextMacrotask(): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, 0))
}

async function buildDirectUploadPayloadBatch(records: ImageRecord[], concurrency: number): Promise<DirectUploadPayload[]> {
    const out: DirectUploadPayload[] = new Array(records.length)
    await mapWithConcurrency(records, concurrency, async (rec, index) => {
        out[index] = await buildDirectUploadPayload(rec)
        if ((index + 1) % 2 === 0) await nextMacrotask()
    })
    return out.filter(Boolean)
}

async function completeDirectUploadChunk(
    entries: DirectUploadPayload[],
    projectCode: string,
): Promise<{ results: UploadRecordResult[]; failedIds: string[] }> {
    if (!entries.length) return { results: [], failedIds: [] }

    const completeRes = await fetchWithTimeout(
        `${editorBaseUrl()}/upload/complete-batch`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                projectCode,
                items: entries.map(({ meta }) => meta),
            }),
        },
        BATCH_UPLOAD_TIMEOUT_MS,
    )

    uploadDebug('[upload][complete-batch]', {
        requestedItems: entries.length,
        status: completeRes.status,
        ok: completeRes.ok,
    })

    if (!completeRes.ok) {
        const txt = await completeRes.text().catch(() => '')
        throw new Error(`Direct upload complete failed (${completeRes.status}): ${txt || completeRes.statusText}`)
    }

    const completeJson = await completeRes.json().catch(() => null)
    const completeItems = Array.isArray(completeJson?.items) ? completeJson.items : []
    const completeById = new Map<string, any>()
    for (const item of completeItems) {
        const id = String(item?.id || '').trim()
        if (id) completeById.set(id, item)
    }

    const results: UploadRecordResult[] = []
    const failedIds: string[] = []
    for (const entry of entries) {
        const item = completeById.get(String(entry.rec.id))
        if (!item) {
            failedIds.push(String(entry.rec.id))
            continue
        }
        const remoteUrl = String(item?.remoteUrl || item?.url || '').trim()
        const uploadState = String(item?.status || (item?.processing ? 'processing' : 'done') || 'done').trim() || 'done'
        let recordPatch: Partial<ImageRecord> | undefined
        if (remoteUrl) {
            recordPatch = await buildRecordPatchFromResponse(entry.rec, item, remoteUrl)
            results.push({
                id: entry.rec.id,
                remoteUrl,
                recordPatch,
            })
        } else if (uploadState !== 'failed') {
            results.push({ id: entry.rec.id, remoteUrl: '', processing: true })
        }
        emitUploadEvent({
            type: 'upload:patched',
            id: String(entry.rec.id),
            patch: {
                ...(recordPatch || {}),
                upload: {
                    ...((((entry.rec as any)?.upload || {}) as any)),
                    state: uploadState,
                    phase: uploadState,
                    progressPct: uploadState === 'done' ? 100 : 0,
                    uploadedBytes: Number((entry.blob as any)?.size || 0) || 0,
                    totalBytes: Number((entry.blob as any)?.size || 0) || 0,
                    ...(remoteUrl ? { remoteUrl } : {}),
                    updatedAt: Date.now(),
                },
            },
        })
    }

    return { results, failedIds }
}

async function uploadBlobWithProgress(
    id: string,
    url: string,
    method: string,
    headers: Record<string, string>,
    body: Blob | File,
    timeoutMs: number,
) {
    if (typeof XMLHttpRequest === 'undefined') {
        return fetchWithTimeout(url, { method, headers, body }, timeoutMs)
    }

    await new Promise<{ status: number; statusText: string; body: string }>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open(method, url, true)
        xhr.timeout = timeoutMs
        for (const [key, value] of Object.entries(headers || {})) {
            if (value != null) xhr.setRequestHeader(key, value)
        }
        xhr.upload.onprogress = (evt) => {
            const totalBytes = Number(evt.total || (body as any)?.size || 0) || 0
            const uploadedBytes = Number(evt.loaded || 0) || 0
            const progressPct = totalBytes > 0 ? Math.max(0, Math.min(100, Math.round((uploadedBytes / totalBytes) * 100))) : 0
            if (!shouldEmitProgressPatch(id, uploadedBytes, totalBytes, progressPct)) return
            emitUploadEvent({
                type: 'upload:patched',
                id,
                patch: {
                    upload: {
                        state: 'uploading',
                        phase: 'uploading',
                        progressPct,
                        uploadedBytes,
                        totalBytes,
                        updatedAt: Date.now(),
                    },
                },
            })
        }
        xhr.onerror = () => reject(new Error('Direct object upload failed (network error)'))
        xhr.ontimeout = () => reject(new Error('Direct object upload failed (timeout)'))
        // Without onabort an aborted request leaves this promise pending forever,
        // holding a concurrency slot. Always reject so the promise settles.
        xhr.onabort = () => reject(new Error('Direct object upload failed (aborted)'))
        xhr.onload = () => resolve({
            status: xhr.status,
            statusText: xhr.statusText,
            body: String(xhr.responseText || ''),
        })
        xhr.send(body)
    }).then((result) => {
        if (result.status < 200 || result.status >= 300) {
            throw new Error(`Direct object upload failed (${result.status}): ${result.body || result.statusText}`)
        }
    })
}

export function markRemoteDeleted(ids: string[]) {
    for (const rawId of ids || []) {
        const id = String(rawId || '').trim()
        if (!id) continue
        pendingRemoteDeleteIds.delete(id)
        settledRemoteDeleteIds.add(id)
    }
    pruneRemoteDeleteSet(settledRemoteDeleteIds)
}

export function markRemoteDeletePending(ids: string[]) {
    for (const rawId of ids || []) {
        const id = String(rawId || '').trim()
        if (!id || settledRemoteDeleteIds.has(id)) continue
        pendingRemoteDeleteIds.add(id)
    }
    pruneRemoteDeleteSet(pendingRemoteDeleteIds)
}

export function clearRemoteDeletePending(ids: string[]) {
    for (const rawId of ids || []) {
        const id = String(rawId || '').trim()
        if (!id) continue
        pendingRemoteDeleteIds.delete(id)
    }
}

export function wasRemoteDeleted(id: string): boolean {
    const wanted = String(id || '').trim()
    return settledRemoteDeleteIds.has(wanted) || pendingRemoteDeleteIds.has(wanted)
}

function buildUploadMeta(rec: ImageRecord, opts?: { width?: number | null; height?: number | null }) {
    const assetId = String((rec as any)?.assetId || (rec as any)?.stockAssetId || '').trim() || null
    const productId = Number((rec as any)?.productId ?? 0)
    const categoryId = Number((rec as any)?.categoryId ?? 0)
    const productTitle = String((rec as any)?.productTitle || (rec as any)?.edits?.preset?.productTitle || '').trim() || undefined
    const categoryTitle = String((rec as any)?.categoryTitle || (rec as any)?.edits?.preset?.categoryTitle || '').trim() || undefined
    const parentCategoryId = Number((rec as any)?.parentCategoryId ?? (rec as any)?.edits?.preset?.parentCategoryId ?? 0) || undefined
    const parentCategoryTitle = String((rec as any)?.parentCategoryTitle || (rec as any)?.edits?.preset?.parentCategoryTitle || '').trim() || undefined
    if (!(categoryId > 0)) {
        throw new Error(`Upload skipped: invalid meta categoryId (${categoryId}) for image ${rec.id}`)
    }

    const prevEdits = (((rec as any)?.edits || {}) as any)
    const prevPreset = (((prevEdits?.preset || {}) as any))
    const frozenOrientation = resolveFrozenOrientation(rec as any)
    const nextEdits = {
        ...prevEdits,
        ...frozenOrientation,
        preset: {
            ...prevPreset,
            ...(productId > 0 ? { productId } : {}),
            ...(productTitle ? { productTitle } : {}),
            ...(categoryId > 0 ? { categoryId } : {}),
            ...(categoryTitle ? { categoryTitle } : {}),
            ...(parentCategoryId ? { parentCategoryId } : {}),
            ...(parentCategoryTitle ? { parentCategoryTitle } : {}),
            ...frozenOrientation,
        },
    }

    const device = resolveUploadDeviceInfo(rec)

    return {
        id: rec.id,
        assetId,
        ...(assetId ? { stockAssetId: assetId, stockId: assetId } : {}),
        name: rec.name,
        categoryId,
        ...(productId > 0 ? { productId } : {}),
        width: opts?.width ?? rec.width ?? null,
        height: opts?.height ?? rec.height ?? null,
        meta: {
            source: String((rec as any)?.upload?.source || '').trim() || 'desktop',
            deviceClass: device.deviceClass,
            deviceOs: device.deviceOs,
            deviceBrowser: device.deviceBrowser,
            ...(assetId ? { assetId, stockAssetId: assetId, stockId: assetId } : {}),
            ...(Array.isArray((rec as any)?.tags) && (rec as any).tags.length ? { tags: [...(rec as any).tags] } : {}),
            edits: nextEdits as any,
        },
    }
}

async function findExistingUploadById(rec: ImageRecord, projectCode: string): Promise<any | null> {
    const base = editorBaseUrl()
    const query = new URLSearchParams({
        projectCode,
        limit: '200',
        offset: '0',
    })
    const categoryId = Number((rec as any)?.categoryId || 0)
    const productId = Number((rec as any)?.productId || 0)
    if (categoryId > 0) query.set('categoryId', String(categoryId))
    if (productId > 0) query.set('productId', String(productId))

    for (let offset = 0; offset <= 2000; offset += 200) {
        query.set('offset', String(offset))
        const res = await fetchWithTimeout(`${base}/upload/list?${query.toString()}`, {}, SINGLE_UPLOAD_TIMEOUT_MS)
        if (!res.ok) break
        const json = await res.json().catch(() => null)
        const items = Array.isArray(json?.items) ? json.items : []
        const match = items.find((item: any) => String(item?.id || '').trim() === String(rec.id))
        if (match) return match
        if (items.length < 200) break
    }

    return null
}

function isUploadBlobLike(value: any): value is Blob | File {
    if (!value || typeof value !== 'object') return false
    if (typeof Blob !== 'undefined' && value instanceof Blob) return true
    if (typeof File !== 'undefined' && value instanceof File) return true
    return typeof value.arrayBuffer === 'function' && typeof value.type === 'string'
}

function getUploadBlob(rec: ImageRecord): Blob | File | null {
    const candidates = [
        (rec as any)?.workingBlob,
        (rec as any)?.originalBlob,
    ]

    for (const candidate of candidates) {
        if (isUploadBlobLike(candidate)) return candidate
    }

    return null
}

function ensureUploadPayload(rec: ImageRecord) {
    const blob = getUploadBlob(rec)
    if (!blob) {
        throw new Error(`Upload skipped: missing upload blob for image ${rec.id}`)
    }

    return {
        rec,
        blob,
        meta: buildUploadMeta(rec),
    }
}

async function buildDirectUploadPayload(rec: ImageRecord): Promise<DirectUploadPayload> {
    const payload = ensureUploadPayload(rec)
    const base = {
        ...payload,
        meta: buildUploadMeta(rec, {
            width: Number(rec.width || 0) > 0 ? Number(rec.width || 0) : null,
            height: Number(rec.height || 0) > 0 ? Number(rec.height || 0) : null,
        }),
    }
    const mime = String(base.blob?.type || (rec as any)?.mime || 'application/octet-stream').trim() || 'application/octet-stream'
    const bytes = Number((base.blob as any)?.size || rec.bytes || 0) || 0

    return {
        ...base,
        meta: {
            ...base.meta,
            mime,
            bytes,
        },
    }
}

async function buildRecordPatchFromResponse(rec: ImageRecord, json: any, remoteUrl: string): Promise<Partial<ImageRecord> | undefined> {
    const previewUrl = String(json?.thumbUrl || json?.previewUrl || '').trim()
    const fullscreenPreviewUrl = String(json?.fullscreenPreviewUrl || '').trim()
    const uploadState = String(json?.status || (json?.processing ? 'processing' : 'done') || 'done').trim() || 'done'
    const responseEdits = json?.meta && typeof json.meta === 'object'
        ? (((json.meta as any)?.edits || undefined) as any)
        : undefined
    const responseCategoryId = Number(json?.categoryId || responseEdits?.preset?.categoryId || 0) || undefined
    const responseProductId = Number(json?.productId || responseEdits?.preset?.productId || 0) || undefined
    const responseCategoryTitle = String(json?.categoryTitle || responseEdits?.preset?.categoryTitle || '').trim() || undefined
    const responseProductTitle = String(json?.productTitle || responseEdits?.preset?.productTitle || '').trim() || undefined
    let recordPatch: Partial<ImageRecord> | undefined = {
        ...(responseCategoryId ? { categoryId: responseCategoryId } : {}),
        ...(responseProductId ? { productId: responseProductId } : {}),
        ...(responseCategoryTitle ? { categoryTitle: responseCategoryTitle } : {}),
        ...(responseProductTitle ? { productTitle: responseProductTitle } : {}),
        upload: {
            ...((((rec as any)?.upload || {}) as any)),
            state: uploadState,
            phase: uploadState,
            ...(previewUrl ? { previewUrl, thumbUrl: previewUrl } : {}),
            ...(fullscreenPreviewUrl ? { fullscreenPreviewUrl } : {}),
            remoteUrl,
            ...(json?.meta && typeof json.meta === 'object' && String((json.meta as any)?.deviceClass || '').trim()
                ? { deviceClass: String((json.meta as any).deviceClass).trim() }
                : {}),
            ...(json?.meta && typeof json.meta === 'object' && String((json.meta as any)?.deviceOs || '').trim()
                ? { deviceOs: String((json.meta as any).deviceOs).trim() }
                : {}),
            ...(json?.meta && typeof json.meta === 'object' && String((json.meta as any)?.deviceBrowser || '').trim()
                ? { deviceBrowser: String((json.meta as any).deviceBrowser).trim() }
                : {}),
        },
    }
    const serverMime = String(json?.mime || '')
    const sourceBlob = getUploadBlob(rec)
    const wasHeic = (!!sourceBlob && isHeicBlob(sourceBlob)) || /heic|heif/i.test(String(rec.mime || ''))
    if (remoteUrl && (wasHeic || !rec.width || !rec.height) && serverMime === 'image/jpeg') {
        recordPatch = {
            ...(recordPatch || {}),
            name: toJpegFileName(rec.name || rec.id),
            mime: 'image/jpeg',
            bytes: Number(json?.bytes || 0) || rec.bytes,
            width: Number(json?.width || 0) || rec.width,
            height: Number(json?.height || 0) || rec.height,
        }
    }

    return recordPatch
}

async function legacyUploadImageRecord(rec: ImageRecord, projectCode: string): Promise<{ remoteUrl: string; recordPatch?: Partial<ImageRecord> }> {
    const payload = ensureUploadPayload(rec)

    const fd = new FormData()

    fd.append('file', payload.blob, rec.name)
    fd.append('projectCode', projectCode)
    fd.append('meta', JSON.stringify(payload.meta))

    const res = await fetchWithTimeout(
        `${editorBaseUrl()}/upload`,
        { method: 'POST', body: fd },
        SINGLE_UPLOAD_TIMEOUT_MS,
    )

    if (!res.ok) {
        if (res.status === 409) {
            const json = await res.json().catch(() => null)
            if (String(json?.error || '') === 'upload_exists') {
                const existing = await findExistingUploadById(rec, projectCode)
                const remoteUrl = String(existing?.remoteUrl || existing?.url || '').trim()
                if (remoteUrl) {
                    const recordPatch = await buildRecordPatchFromResponse(rec, existing, remoteUrl)
                    return { remoteUrl, recordPatch }
                }
            }
        }
        const txt = await res.text().catch(() => '')
        throw new Error(`Upload failed (${res.status}): ${txt || res.statusText}`)
    }

    const json = await res.json().catch(() => null)
    const remoteUrl = json?.remoteUrl || json?.url
    if (!remoteUrl) throw new Error('Upload ok, aber keine remoteUrl im Response')

    const recordPatch = await buildRecordPatchFromResponse(rec, json, remoteUrl)

    return { remoteUrl, recordPatch }
}

async function legacyUploadImageRecordsBatch(recs: ImageRecord[], projectCode: string): Promise<UploadRecordResult[]> {
    const seen = new Set<string>()
    const batch = (recs || [])
        .filter(Boolean)
        .filter((rec) => {
            const id = String((rec as any)?.id || '').trim()
            if (!id || seen.has(id)) return false
            seen.add(id)
            return true
        })
        .map((rec) => ensureUploadPayload(rec))
    if (!batch.length) return []

    const fd = new FormData()
    fd.append('projectCode', projectCode)
    fd.append('metas', JSON.stringify(batch.map(({ meta }) => meta)))

    for (const { rec, blob } of batch) {
        fd.append('files[]', blob, rec.name)
    }

    const res = await fetchWithTimeout(
        `${editorBaseUrl()}/upload/batch`,
        { method: 'POST', body: fd },
        BATCH_UPLOAD_TIMEOUT_MS,
    )
    if (!res.ok) {
        const txt = await res.text().catch(() => '')
        throw new Error(`Batch upload failed (${res.status}): ${txt || res.statusText}`)
    }

    const json = await res.json().catch(() => null)
    const items = Array.isArray(json?.items) ? json.items : []
    const byId = new Map<string, any>()
    for (const item of items) {
        const id = String(item?.id || '').trim()
        if (id) byId.set(id, item)
    }

    const out: UploadRecordResult[] = []
    for (const { rec } of batch) {
        const item = byId.get(String(rec.id))
        const remoteUrl = String(item?.remoteUrl || item?.url || '').trim()
        if (!remoteUrl) {
            throw new Error(`Batch upload response missing remoteUrl for image ${rec.id}`)
        }
        const recordPatch = await buildRecordPatchFromResponse(rec, item, remoteUrl)
        out.push({
            id: rec.id,
            remoteUrl,
            recordPatch,
        })
    }

    return out
}

async function directUploadBatch(recs: ImageRecord[], projectCode: string): Promise<UploadRecordResult[]> {
    if (directUploadAvailability === 'unavailable') {
        throw new Error('Direct upload unavailable for current editor backend.')
    }

    const seen = new Set<string>()
    const deduped = (recs || [])
        .filter(Boolean)
        .filter((rec) => {
            const id = String((rec as any)?.id || '').trim()
            if (!id || seen.has(id)) return false
            seen.add(id)
            return true
        })
    if (!deduped.length) return []

    const putConcurrency = resolveAdaptiveDirectPutConcurrency(deduped)
    const payloadBuildConcurrency = deduped.length >= 24
        ? 1
        : Math.max(1, Math.min(2, Math.ceil(putConcurrency / 3)))
    const immediate: UploadRecordResult[] = []
    const completed: UploadRecordResult[] = []
    const failedIds: string[] = []
    const completionPromises: Promise<void>[] = []
    let pendingCompletionEntries: DirectUploadPayload[] = []
    let completionFlushTimer: ReturnType<typeof setTimeout> | null = null
    const wantedOrder = new Map<string, number>()
    deduped.forEach((rec, index) => wantedOrder.set(String((rec as any)?.id || ''), index))

    const startCompletionChunk = (entries: DirectUploadPayload[]) => {
        if (!entries.length) return
        completionPromises.push(
            completeDirectUploadChunk(entries, projectCode)
                .then(({ results, failedIds: completeFailedIds }) => {
                    completed.push(...results)
                    // Any ids that succeeded in this chunk must not stay marked failed
                    // (a PUT-level failure could have flagged an id that the server
                    // later accepted). Clear them before recording per-chunk failures.
                    const succeededIds = new Set(results.map((r) => String(r.id)))
                    if (succeededIds.size) {
                        for (let i = failedIds.length - 1; i >= 0; i--) {
                            if (succeededIds.has(failedIds[i]!)) failedIds.splice(i, 1)
                        }
                    }
                    for (const id of completeFailedIds) {
                        if (!failedIds.includes(id)) failedIds.push(id)
                    }
                })
                .catch((error) => {
                    // A completion-chunk failure must only mark THIS chunk's ids as
                    // failed. It must NOT re-throw: re-throwing previously caused the
                    // whole batch to abort and discard already-successful partialResults
                    // and the accurate failedIds accounting. We swallow here and let the
                    // caller surface a PartialBatchUploadError with correct failedIds.
                    uploadDebug('[upload][complete-batch][error]', {
                        chunkIds: entries.map((entry) => String(entry?.rec?.id || '')),
                        message: error instanceof Error ? error.message : String(error),
                    })
                    for (const entry of entries) {
                        const id = String(entry?.rec?.id || '').trim()
                        if (id && !failedIds.includes(id)) failedIds.push(id)
                    }
                }),
        )
    }

    const flushPendingCompletions = (forceAll = false) => {
        if (completionFlushTimer) {
            clearTimeout(completionFlushTimer)
            completionFlushTimer = null
        }
        while (pendingCompletionEntries.length >= COMPLETE_BATCH_CHUNK_SIZE) {
            startCompletionChunk(pendingCompletionEntries.splice(0, COMPLETE_BATCH_CHUNK_SIZE))
        }
        if (forceAll && pendingCompletionEntries.length) {
            startCompletionChunk(pendingCompletionEntries.splice(0, pendingCompletionEntries.length))
        }
    }

    const scheduleCompletionFlush = () => {
        if (pendingCompletionEntries.length >= COMPLETE_BATCH_CHUNK_SIZE) {
            flushPendingCompletions(false)
            return
        }
        if (completionFlushTimer) return
        completionFlushTimer = setTimeout(() => {
            completionFlushTimer = null
            flushPendingCompletions(true)
        }, COMPLETE_BATCH_FLUSH_DELAY_MS)
    }

    const uploadWaveSize = deduped.length >= 24
        ? Math.max(3, Math.min(6, putConcurrency + 1))
        : Math.max(4, Math.min(8, putConcurrency + 2))
    const waves = chunkArray(deduped, uploadWaveSize)
    for (const wave of waves) {
        const batch = await buildDirectUploadPayloadBatch(wave, payloadBuildConcurrency)
        if (!batch.length) continue

        for (const entry of batch) {
            emitUploadEvent({
                type: 'upload:patched',
                id: String(entry.rec.id),
                patch: {
                    upload: {
                        state: 'uploading',
                        phase: 'presigning',
                        progressPct: 0,
                        uploadedBytes: 0,
                        totalBytes: Number((entry.blob as any)?.size || 0) || 0,
                        updatedAt: Date.now(),
                    },
                },
            })
        }

        const presignRes = await fetchWithTimeout(
            `${editorBaseUrl()}/upload/presign-batch`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    projectCode,
                    items: batch.map(({ meta }) => meta),
                }),
            },
            BATCH_UPLOAD_TIMEOUT_MS,
        )

        uploadDebug('[upload][presign-batch]', {
            requestedItems: batch.length,
            itemIds: batch.map(({ rec }) => String(rec.id)),
            status: presignRes.status,
            ok: presignRes.ok,
        })

        if (!presignRes.ok) {
            const txt = await presignRes.text().catch(() => '')
            if (presignRes.status === 400 && txt.includes('direct_upload_unavailable')) {
                directUploadAvailability = 'unavailable'
            }
            throw new Error(`Direct upload presign failed (${presignRes.status}): ${txt || presignRes.statusText}`)
        }

        directUploadAvailability = 'available'

        const presignJson = await presignRes.json().catch(() => null)
        const presignedItems = Array.isArray(presignJson?.items) ? (presignJson.items as PresignBatchItemResponse[]) : []
        uploadDebug('[upload][presign-batch][response]', {
            receivedItems: presignedItems.length,
            directPutConcurrency: putConcurrency,
            waveSize: batch.length,
        })
        const presignedById = new Map<string, PresignBatchItemResponse>()
        for (const item of presignedItems) {
            const id = String((item as any)?.id || (item as any)?.item?.id || '').trim()
            if (id) presignedById.set(id, item)
        }

        await mapWithConcurrency(batch, putConcurrency, async (entry) => {
            let countedPut = false
            try {
                const presigned = presignedById.get(String(entry.rec.id))
                if (!presigned) {
                    throw new Error(`Direct upload presign response missing item ${entry.rec.id}`)
                }

                if (presigned.existing) {
                    const remoteUrl = String(presigned.item?.remoteUrl || presigned.item?.url || '').trim()
                    if (!remoteUrl) {
                        throw new Error(`Direct upload existing item missing remoteUrl for image ${entry.rec.id}`)
                    }
                    const recordPatch = await buildRecordPatchFromResponse(entry.rec, presigned.item, remoteUrl)
                    immediate.push({
                        id: entry.rec.id,
                        remoteUrl,
                        recordPatch,
                    })
                    return
                }

                activeDirectPutCount += 1
                countedPut = true
                uploadDebug('[upload][put][start]', {
                    id: entry.rec.id,
                    activePuts: activeDirectPutCount,
                    method: presigned.uploadMethod || 'PUT',
                    hasHeaders: Object.keys(presigned.uploadHeaders || {}).length > 0,
                    url: presigned.uploadUrl,
                })
                await uploadBlobWithProgress(
                    String(entry.rec.id),
                    presigned.uploadUrl,
                    presigned.uploadMethod || 'PUT',
                    presigned.uploadHeaders || {},
                    entry.blob,
                    BATCH_UPLOAD_TIMEOUT_MS,
                )
                uploadDebug('[upload][put][done]', {
                    id: entry.rec.id,
                    status: 200,
                })

                emitUploadEvent({
                    type: 'upload:patched',
                    id: String(entry.rec.id),
                    patch: {
                        upload: {
                            state: 'uploading',
                            phase: 'completing',
                            progressPct: 100,
                            uploadedBytes: Number((entry.blob as any)?.size || 0) || 0,
                            totalBytes: Number((entry.blob as any)?.size || 0) || 0,
                            updatedAt: Date.now(),
                        },
                    },
                })
                pendingCompletionEntries.push(entry)
                scheduleCompletionFlush()
            } catch {
                const id = String(entry?.rec?.id || '').trim()
                if (id && !failedIds.includes(id)) failedIds.push(id)
            } finally {
                if (countedPut && activeDirectPutCount > 0) activeDirectPutCount -= 1
            }
        })

        await nextMacrotask()
    }

    flushPendingCompletions(true)

    // completionPromises never reject now (startCompletionChunk swallows errors and
    // records failed ids instead), so allSettled simply waits for all chunks to finish.
    // Partial failures are surfaced below via PartialBatchUploadError with accurate
    // failedIds, preserving any already-successful results in partialResults.
    await Promise.allSettled(completionPromises)

    if (!completionPromises.length && !completed.length) {
        if (failedIds.length) {
            throw new PartialBatchUploadError(
                'Direct upload failed for one or more items.',
                immediate.sort((a, b) => (wantedOrder.get(a.id) ?? 0) - (wantedOrder.get(b.id) ?? 0)),
                failedIds,
            )
        }
        return immediate.sort((a, b) => (wantedOrder.get(a.id) ?? 0) - (wantedOrder.get(b.id) ?? 0))
    }

    const partialResults = [...immediate, ...completed].sort((a, b) => (wantedOrder.get(a.id) ?? 0) - (wantedOrder.get(b.id) ?? 0))
    if (failedIds.length) {
        throw new PartialBatchUploadError('Direct upload failed for one or more items.', partialResults, failedIds)
    }

    return partialResults
}

function canUseDirectUpload(rec: ImageRecord): boolean {
    const blob = getUploadBlob(rec)
    if (!blob) return false
    return !isHeicBlob(blob)
}

export async function uploadImageRecord(rec: ImageRecord, projectCode: string): Promise<{ remoteUrl: string; recordPatch?: Partial<ImageRecord> }> {
    if (canUseDirectUpload(rec) && directUploadAvailability !== 'unavailable') {
        try {
            const [result] = await directUploadBatch([rec], projectCode)
            if (result) return result
        } catch (error) {
            throw error instanceof Error
                ? error
                : new Error('Direct upload failed.')
        }
    }

    if (canUseDirectUpload(rec) && directUploadAvailability === 'unavailable') {
        throw new Error('Direct upload is unavailable for this editor backend.')
    }

    throw new Error('Legacy upload fallback is disabled. This editor only supports direct uploads.')
}

export async function uploadImageRecordsBatch(recs: ImageRecord[], projectCode: string): Promise<UploadRecordResult[]> {
    const directEligible = (recs || []).filter((rec) => canUseDirectUpload(rec))
    const fallbackEligible = (recs || []).filter((rec) => !canUseDirectUpload(rec))
    const out: UploadRecordResult[] = []

    if (directEligible.length && directUploadAvailability !== 'unavailable') {
        try {
            out.push(...await directUploadBatch(directEligible, projectCode))
        } catch (error) {
            if (error instanceof PartialBatchUploadError) {
                out.push(...error.partialResults)
                const failed = directEligible.filter((rec) => error.failedIds.includes(String(rec.id)))
                if (failed.length) {
                    throw error
                }
            } else {
                throw error instanceof Error
                    ? error
                    : new Error('Direct upload failed.')
            }
        }
    } else if (directEligible.length) {
        throw new Error('Direct upload is unavailable for this editor backend.')
    }

    if (fallbackEligible.length) {
        throw new Error('Legacy upload fallback is disabled. This editor only supports direct uploads.')
    }

    const wantedOrder = new Map<string, number>()
    ;(recs || []).forEach((rec, index) => wantedOrder.set(String((rec as any)?.id || ''), index))
    return out.sort((a, b) => (wantedOrder.get(a.id) ?? 0) - (wantedOrder.get(b.id) ?? 0))
}

export async function deleteImageRecordRemote(rec: ImageRecord, projectCode: string): Promise<void> {
    void projectCode
    await deleteImageRecordsRemoteBulk([String(rec?.id || '')])
}

/** Ein Design (Server: DesignDocumentRepository::describeForCustomer), das Bilder noch verwendet. */
export type BlockingDesign = {
    id: string
    name: string | null
    productId: number
    categoryId: number
    productTitle: string | null
    createdAt: string
}

/**
 * Löschen (teilweise) abgelehnt, weil Bilder in einem Design stecken. `deletedIds` sind trotzdem
 * gelöscht worden; `blockedIds` bleiben auf dem Server und dürfen lokal NICHT entfernt werden.
 */
export class UploadInUseError extends Error {
    readonly blockedIds: string[]
    readonly deletedIds: string[]
    readonly designs: BlockingDesign[]
    constructor(blockedIds: string[], deletedIds: string[], designs: BlockingDesign[]) {
        super('upload_referenced_by_design')
        this.name = 'UploadInUseError'
        this.blockedIds = blockedIds
        this.deletedIds = deletedIds
        this.designs = designs
    }
}

export function isUploadInUseError(e: unknown): e is UploadInUseError {
    return e instanceof UploadInUseError
}

function parseBlockedInfo(json: any): { blockedIds: string[]; designs: BlockingDesign[] } {
    const fromReferenced = json?.referenced && typeof json.referenced === 'object' ? Object.keys(json.referenced) : []
    const blockedIds = (Array.isArray(json?.blockedIds) ? json.blockedIds : fromReferenced)
        .map((id: any) => String(id || '').trim()).filter(Boolean)
    const designs: BlockingDesign[] = (Array.isArray(json?.designs) ? json.designs : []).map((d: any) => ({
        id: String(d?.id || ''),
        name: d?.name ? String(d.name) : null,
        productId: Number(d?.productId || 0),
        categoryId: Number(d?.categoryId || 0),
        productTitle: d?.productTitle ? String(d.productTitle) : null,
        createdAt: String(d?.createdAt || ''),
    })).filter((d: BlockingDesign) => d.id)
    return { blockedIds, designs }
}

/** Anzeigename eines Designs für Kundenmeldungen. */
export function blockingDesignLabel(d: BlockingDesign): string {
    if (d.name) return d.name
    const date = d.createdAt ? new Date(d.createdAt) : null
    const dateLabel = date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('de-DE') : ''
    const base = d.productTitle || 'Design'
    return dateLabel ? `${base} vom ${dateLabel}` : base
}

/** Kurztext für Toasts (Stellen ohne eigenen Dialog). */
export function describeUploadInUse(e: UploadInUseError): string {
    const n = e.blockedIds.length
    const names = e.designs.map((d) => `„${blockingDesignLabel(d)}“`).join(', ')
    const what = n === 1 ? 'Das Bild wird' : `${n} Bilder werden`
    return `${what} noch im Design ${names || '(Designer)'} verwendet und ${n === 1 ? 'kann' : 'können'} nicht gelöscht werden.`
}

export async function deleteImageRecordsRemoteBulk(ids: string[]): Promise<string[]> {
    const wanted = Array.from(new Set((ids || []).map((id) => String(id || '').trim()).filter(Boolean)))
    if (!wanted.length) return []
    const pending = wanted.filter((id) => !settledRemoteDeleteIds.has(id))
    if (!pending.length) return wanted
    markRemoteDeletePending(pending)

    const cfg = getWidgetConfig() as any
    const base = String(cfg?.editorBaseUrl || editorBaseUrl() || '').replace(/\/+$/, '')
    const url = `${base}/upload/bulk-delete`

    try {
        const res = await fetchWithTimeout(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ids: pending,
            }),
        }, SINGLE_UPLOAD_TIMEOUT_MS)

        if (res.status === 404) {
            markRemoteDeleted(pending)
            return wanted
        }
        if (res.status === 409) {
            clearRemoteDeletePending(pending)
            const json = await res.json().catch(() => null)
            if (String(json?.error || '') === 'upload_referenced_by_design') {
                const info = parseBlockedInfo(json)
                throw new UploadInUseError(info.blockedIds.length ? info.blockedIds : pending, [], info.designs)
            }
            throw new Error(`Delete failed (409): ${json?.message || res.statusText}`)
        }
        if (!res.ok) {
            clearRemoteDeletePending(pending)
            const txt = await res.text().catch(() => '')
            throw new Error(`Delete failed (${res.status}): ${txt || res.statusText}`)
        }

        const json = await res.json().catch(() => null)
        const deletedIds = Array.isArray(json?.deletedIds) ? json.deletedIds.map((id: any) => String(id || '').trim()).filter(Boolean) : []
        const blocked = parseBlockedInfo(json)
        if (blocked.blockedIds.length) {
            const blockedSet = new Set(blocked.blockedIds)
            const done = (deletedIds.length ? deletedIds : pending).filter((id: string) => !blockedSet.has(id))
            clearRemoteDeletePending(blocked.blockedIds)
            markRemoteDeleted(done)
            throw new UploadInUseError(blocked.blockedIds, done, blocked.designs)
        }
        markRemoteDeleted(deletedIds.length ? deletedIds : pending)
        return deletedIds.length ? deletedIds : wanted
    } catch (e) {
        clearRemoteDeletePending(pending)
        throw e
    }
}
